<template>
  <div>
    <div class="table-actions">
      <v-row>
        <v-col class="pb-0">
          <v-text-field
            v-model="search"
            :label="$t('message.businessAddresses.list.searchPlaceholder')"
            prepend-inner-icon="icon-search"
            clearable
            outlined
            rounded
            class="search-field"
            :hide-details="true"
            @click:clear="search = ''"
          >
          </v-text-field>
          <div class="facilities-filters">
            <FacilitiesFilterSelect
              :options="openingStatusOptions"
              :prefix="`${$t('message.common.facilityOpenStatus')}:`"
              @update="openingStatusFilter = $event"
            />
            <FacilitiesFilterSelect
              :options="importStatusOptions"
              :prefix="`${$t('message.common.importStatus')}:`"
              @update="importStatusFilter = $event"
            />
          </div>
        </v-col>
      </v-row>
    </div>
    <v-row>
      <v-col class="pt-0">
        <Pagination
          :page="page"
          :per-page="itemsPerPage"
          :options="perPageItems"
          :total-count="totalCount"
          :total-visible="5"
          @change="handlePaginationChange"
        >
          <FacilitiesSortingSelect @update="sortingOption = $event" />
        </Pagination>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <InfoBox
          class="mt-2"
          :text="$t('message.companyProfile.listings.refreshInfo')"
        />
      </v-col>
    </v-row>

    <div class="table-filters">
      <div
        class="select-all"
        v-if="currentPageItems.length > 0"
        @click="handleToggleAllFacilities"
      >
        <div class="mr-4">
          <v-checkbox :input-value="areAllFacilitiesSelected">
            <template #label>
              <span class="select-all__label">{{
                $t("message.common.selectAll")
              }}</span>
            </template>
          </v-checkbox>
        </div>

        <ItemsCounter
          v-if="selectedItems.length"
          :count="selectedItems.length"
        />
      </div>
      <div class="d-flex align-center">
        <v-checkbox
          class="select-all__label"
          :input-value="ignoreUnverifiedFacilities"
          @change="$emit('unverified-facilities-changed', $event)"
        >
          <template #label>
            <span class="select-all__label">{{
              $t("message.businessAddresses.list.ignoreUnverifiedFacilities")
            }}</span>
          </template></v-checkbox
        >
      </div>
    </div>
    <div v-if="currentPageItems.length > 0">
      <v-list-item
        v-for="item in currentPageItems"
        :key="item.id"
        :ripple="false"
        class="list-item"
        @click="handleToggleFacility(item)"
      >
        <v-checkbox
          :disabled="!item.isSelectable && !isDeleteMode"
          :input-value="
            !item.isSelectable && !isDeleteMode
              ? item.isSelected
              : isFacilitySelected(item.id)
          "
          class="mr-2"
        ></v-checkbox>
        <v-list-item-content class="d-flex justify-space-between">
          <div class="list-item__content-primary">
            <p class="list-item__name">{{ item.name }}</p>
            <div class="list-item__description">
              <p class="list-item__address">
                {{ item.full_address }}
              </p>
              <div v-if="item.storeCode">|</div>
              <p v-if="item.storeCode" class="list-item__store-code">
                {{ `${$t("message.common.storeCode")}: ${item.storeCode}` }}
              </p>
            </div>
          </div>
          <div v-if="getStatusType(item)" class="facility-status">
            <v-icon class="mr-1" size="15"> icon-info </v-icon
            >{{ getStatusType(item) }}
          </div>
        </v-list-item-content>
      </v-list-item>
    </div>
    <no-data v-else></no-data>
    <v-row>
      <v-col class="pb-0">
        <Pagination
          :page="page"
          :per-page="itemsPerPage"
          :options="perPageItems"
          :show-options="false"
          :total-count="totalCount"
          :total-visible="5"
          @change="handlePaginationChange"
        ></Pagination>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import Pagination from "@/components/List/Pagination";
import ItemsCounter from "@/components/List/ItemsCounter";
import NoData from "@/components/ui/NoData";
import FacilitiesSortingSelect from "@/components/ListingsConfiguration/FacilitiesSortingSelect.vue";
import FacilitiesFilterSelect from "@/components/ListingsConfiguration/FacilitiesFilterSelect.vue";
import InfoBox from "@/components/ui/InfoBox.vue";

import { scrollToTop } from "@/helpers/helpers";

export default {
  name: "BusinessAddressesTable",
  components: {
    Pagination,
    ItemsCounter,
    NoData,
    FacilitiesSortingSelect,
    FacilitiesFilterSelect,
    InfoBox
  },
  props: {
    businessAddresses: {
      type: Object,
      required: true
    },
    preSelection: {
      type: Boolean,
      default: false
    },
    isDeleteMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      page: 1,
      perPageItems: [5, 10, 25, 100],
      itemsPerPage: 10,
      search: "",
      selectedItems: [],
      listItemHeight: 66,
      sortingOption: null,
      ignoreUnverifiedFacilities: true,
      openingStatusOptions: [
        { text: this.$t("message.common.openStatusLabel.all"), value: null },
        { text: this.$t("message.common.openStatusLabel.open"), value: 1 },
        {
          text: this.$t("message.common.openStatusLabel.closedTemporarily"),
          value: 3
        },
        {
          text: this.$t("message.common.openStatusLabel.closedPermanently"),
          value: 2
        }
      ],
      importStatusOptions: [
        { text: this.$t("message.common.importStatusLabel.any"), value: null },
        {
          text: this.$t("message.common.importStatusLabel.imported"),
          value: true
        },
        {
          text: this.$t("message.common.importStatusLabel.notImported"),
          value: false
        }
      ],
      importStatusFilter: null,
      openingStatusFilter: null
    };
  },

  watch: {
    selectedItems() {
      this.$emit(
        "selected",
        this.isDeleteMode
          ? this.selectedItems
          : this.selectedItems.filter(item => !item.isSelected) // for import, set as selected only these not selected initially
      );
    }
  },

  computed: {
    items() {
      return this.businessAddresses && this.businessAddresses.items
        ? this.businessAddresses.items
            .filter(this.filterItems)
            .map(this.mapItems)
            .sort(this.sortItems)
        : [];
    },
    filteredItems() {
      return !this.search
        ? this.items
        : this.items.filter(
            item =>
              item.full_address
                .toLowerCase()
                .indexOf(this.search.toLowerCase()) >= 0 ||
              item.name.toLowerCase().indexOf(this.search.toLowerCase()) >= 0 ||
              item.storeCode.toLowerCase().indexOf(this.search.toLowerCase()) >=
                0
          );
    },

    totalCount() {
      return this.filteredItems ? this.filteredItems.length : 0;
    },

    currentPageItems() {
      return this.filteredItems.length > this.itemsPerPage
        ? this.filteredItems.slice(
            (this.page - 1) * this.itemsPerPage,
            this.page * this.itemsPerPage
          )
        : this.filteredItems;
    },

    areAllFacilitiesSelected() {
      return this.filteredItems
        .filter(item => item.isSelectable)
        .every(item => this.isFacilitySelected(item.id));
    },

    virtualScrollHeight() {
      return (
        Math.floor(window.innerHeight / this.listItemHeight) *
          this.listItemHeight +
        1
      );
    }
  },

  created() {
    this.businessAddresses.items.forEach(businessAddress => {
      if (businessAddress.isSelected && this.preSelection) {
        this.selectedItems.push(businessAddress);
      }
    });
  },

  methods: {
    filterItems(item) {
      let filterCondition = true;
      if (this.openingStatusFilter !== null) {
        filterCondition =
          filterCondition && item.openingStatus === this.openingStatusFilter;
      }
      if (this.importStatusFilter !== null) {
        filterCondition =
          filterCondition && item.isImported === this.importStatusFilter;
      }
      return filterCondition;
    },
    mapItems(businessAddress) {
      return {
        ...businessAddress,
        isSelectable: this.isDeleteMode
          ? true
          : !businessAddress.isNonSelectable &&
            !businessAddress.isImported &&
            !businessAddress.isSelected // condition for facilities import
      };
    },
    sortItems(a, b) {
      const aValue = this.obtainSortingValue(a);
      const bValue = this.obtainSortingValue(b);

      if (bValue.length === 0) {
        return -1;
      }
      if (aValue.length === 0) {
        return 1;
      }
      return aValue > bValue ? 1 : bValue > aValue ? -1 : 0;
    },
    getStatusType({ isDisconnected, isDuplicate }) {
      const statusText = this.$t("message.businessAddresses.list.facilityIs");
      if (isDisconnected) {
        return `${statusText} ${this.$t(
          "message.businessAddresses.list.isDisconnected"
        )}`;
      }
      if (isDuplicate) {
        return `${statusText} ${this.$t(
          "message.businessAddresses.list.isDuplicate"
        )}`;
      }
    },
    isFacilitySelected(facilityId) {
      return this.selectedItems.some(({ id }) => facilityId === id);
    },

    handleToggleAllFacilities() {
      const facilitiesToToggle = this.areAllFacilitiesSelected
        ? this.filteredItems.filter(
            item => item.isSelectable && this.isFacilitySelected(item.id)
          )
        : this.filteredItems.filter(
            item => item.isSelectable && !this.isFacilitySelected(item.id)
          );

      facilitiesToToggle.forEach(item => this.handleToggleFacility(item));
    },

    handleToggleFacility(facility) {
      if (!facility.isSelectable) return;

      const selected = [...this.selectedItems];
      const foundIndex = selected.findIndex(({ id }) => {
        return id === facility.id;
      });

      if (foundIndex !== -1) {
        selected.splice(foundIndex, 1);
      } else {
        selected.push(facility);
      }

      this.selectedItems = selected;
    },

    obtainSortingValue(rawValue) {
      if (!this.sortingOption && rawValue.id) return rawValue.id;

      const path = this.sortingOption.split("-");
      let value = rawValue;

      path.forEach(pathPart => {
        if (pathPart in value) {
          value = value[pathPart];
        }
      });

      return value;
    },

    handlePaginationChange({ page, perPage }) {
      this.page = page;
      this.itemsPerPage = perPage;

      this.$nextTick(() => {
        scrollToTop(this.$refs.list.$el);
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.table-actions {
  top: 0;
  position: sticky;
  background: white;
  z-index: 1;
  padding-bottom: $spacing * 1.5;
}

.facility-status {
  @include font-size(12px);
  color: $dark-blue-3;
  display: flex;
  align-items: center;
}

.facilities-filters {
  margin-top: $spacing * 1.5;
  display: flex;
  gap: $spacing * 2.5;
}

.table-filters {
  display: flex;
  justify-content: space-between;
}

.search-input {
  margin-bottom: $spacing * 2;
  width: 100%;
  max-width: 300px;
}

.select-input {
  margin-left: 48px;
  max-width: 120px;
  width: 100%;
}

.select-all {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 $spacing * 2;
  cursor: pointer;
  transition: background 0.2s ease-in;

  &:hover {
    background-color: $light-4;
  }

  &__label {
    @include font-size(14px);
    font-weight: 500;
    margin-left: $spacing * 0.7;
    color: $text;
  }
}

.no-data {
  height: 400px;
}

.v-list-item__content > * {
  flex: unset;
}

.list-item {
  border-bottom: 1px solid $light-3;

  &__content-primary {
    max-width: calc(100% - 415px);
    text-overflow: ellipsis;
    overflow: hidden;
  }

  &__name,
  &__description {
    color: $dark;
  }

  &__name {
    @include font-size(14px);
    color: $text;
    font-weight: 500;
    margin-top: $spacing * 0.5;
    margin-bottom: 0;
  }

  &__description {
    position: relative;
    display: flex;
    @include font-size(12px);
    font-weight: 400;
    margin-bottom: 0;
    max-width: 100%;
    white-space: nowrap;
    color: $text;

    & > p {
      margin-bottom: 0;
    }
  }

  &__address {
    margin-right: $spacing;
    position: relative;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  &__store-code {
    margin-left: $spacing * 1.25;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  &__content-secondary {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 35%;
    margin-right: $spacing * 4;
  }

  &__statuses {
    display: flex;
    flex-direction: column;
  }
}
</style>
